import { useRef, useState } from "react";
import { BELGE_TURU_ADI, hedefEsit, malzemeAralikMetni, type GelenHedef, type MalzemeKartSertifikasi, type MalzemeKarti, type MalzemeKayitAlani, type MalzemeKayitSatiri } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  MALZEME_UCU,
  OKUMA_UCU,
  malzemeCinsMetni,
  malzemeDurumMetni,
  malzemeFormu,
  malzemeHaritaMetni,
  malzemeKarsilastirmaSatirlari,
  malzemeKayitMetni,
  malzemeNedenMetni,
  malzemeSertifikaMetni,
  malzemeYenidenMetni,
  okumaDugmesi,
  okumaEylemleri,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 4 · PR 21: Documents kartının malzeme sertifikası "Document reading" bölümü (prototip belgeler--malzeme-okuma, kart 1-4; `TeslimOkumaBolumu` ve `KalibrasyonOkumaBolumu`
 * iskeleti). Okuma yalnız kapak tanımasının, eşlemenin, kuralların, "Use the reading"in, "Add to package"in ve "Record on the package"ın kullandığı değerleri gösterir (sözleşme §13):
 * kapakta 4 işaret ve müşteri PO; sertifika başına PN, tür, tanık imzası, no ve lot anahtarı; heat satırları; yalnız sınırı basılı sonuç satırları; kitapta indeks satırları.
 * Datapack ve OEM MRB yalnız kapaktan tanınır (okunmaz). OASIS her sertifikanın stok paketini heat, değirmen lotu ya da lot anahtarıyla birebir önerir. "Use this place" kartın
 * bugünkü tür+yer kaydını çağırır; "Use these pages" ve "Use the reading" yalnız dosyalama formunu doldurur (sunucuya yazmaz). Dosyalamadan sonra öteki sertifikalar "Add to package"
 * ile tek tek eklenir; paketin boş heat / lot / SN ve PO alanı yalnız PM ya da sistem yöneticisinin "Record heat" / "Record PO" onayıyla dolar (iki heat adayında seçim kişinindir,
 * seçili gelmez). `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Add to package" ve "Record on the package" sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface MalzemeFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin sertifika no) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use these pages" / "Use the reading": Pages alanı (Partial) */
  sayfalar: (s: string) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function MalzemeOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: MalzemeKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: MalzemeFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const [heatSecimi, setHeatSecimi] = useState<Record<number, string>>({});
  const [secim, setSecim] = useState<number | null>(null);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const cok = k.sertifikalar.length > 1;
  const kapakli = o?.dosyaCinsi === "datapack" || o?.dosyaCinsi === "oem_mrb";
  /** M3-23: sertifika seçicisi yalnız yeri olan sertifikalar; varsayılan kartın yerindeki ilk sertifika, yoksa ilk yerli sertifika. */
  const secilebilir = k.sertifikalar.filter((c) => c.yer.durum !== "yok" && c.yer.yer);
  const varsayilan = (secilebilir.find((c) => hedefEsit(c.yer.yer, hedef)) ?? secilebilir[0] ?? k.sertifikalar[0])?.sira ?? null;
  const secili = k.sertifikalar.some((c) => c.sira === secim) ? secim : varsayilan;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = malzemeKarsilastirmaSatirlari(k);
  const yeniden = malzemeYenidenMetni(k.yenidenKarsilastirma);
  const toplamUyari = (k.karsilastirma?.uyarilar.length ?? 0) + k.sertifikalar.reduce((t, c) => t + c.uyarilar.length, 0);
  const kirmiziSertifika = k.sertifikalar.find((c) => c.uyarilar.some((u) => u.renk === "kirmizi" && !u.karar));
  const hedefPaket = hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? k.sertifikalar.find((c) => hedefEsit(c.yer.yer, hedef))?.yer.paket ?? null : null;
  const mtl01 = k.sertifikalar.some((c) => c.uyarilar.some((u) => u.kod === "MTL-01" && !u.karar));
  const turFarki = k.sertifikalar.find((c) => c.uyarilar.some((u) => u.kod === "GEN-04") && c.turOnerisi);
  const gen11 = k.sertifikalar.some((c) => c.uyarilar.some((u) => u.kod === "GEN-11"));
  const ekleVar = k.sertifikalar.some((c) => c.durum === "eklenebilir" || c.durum === "dosyalanmadi");
  const kayitVar = k.sertifikalar.some((c) => c.kayit);

  const paketHucresi = (c: MalzemeKartSertifikasi) => {
    const p = c.yer.paket;
    const tamPdf = !cok && c.sayfalar.bas === 1 && o?.sayfaSayisi === c.sayfalar.son;
    return (
      <>
        {p && c.yer.durum !== "yok" && (
          <>
            <span className="mr-1.5">
              {[p.pkg, p.pn, p.iz ? cevir("heat / lot / SN {0}", p.iz) : cevir("no heat / lot / SN"), p.poNo ? `PO ${p.poNo}` : cevir("no PO"), `${p.gelenMiktar} ${p.birim}`].join(" · ")}
            </span>
            <Etiket tone={durumTonu(c.yer.durum)}>{c.yer.durum === "kisi" ? cevir("your choice") : cevir("proposed")}</Etiket>
            {c.yer.durum === "oneri" && k.karsilastirma?.notlar.some((n) => n.tur === "pn_bos_iz" && n.sertifika === c.sira) && (
              <span className="block text-xs text-ink-3">{cevir("the only package of {0} without heat / lot / SN", p.pn)}</span>
            )}
          </>
        )}
        {(!p || c.yer.durum === "yok") && <span className="text-ink-2">{malzemeNedenMetni(c.yer.neden)}</span>}
        {yardim && c.yer.durum === "oneri" && c.yer.yer && !hedefEsit(c.yer.yer, hedef) && (
          <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${c.sira}`} onClick={() => calistir(`yer-${c.sira}`, () => yardim.yerKaydet(c.yer.yer!))}>{cevir("Use this place")}</Buton>
        )}
        {yardim && c.yer.durum !== "yok" && !tamPdf && (
          <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} onClick={() => yardim.sayfalar(malzemeAralikMetni(c.sayfalar))}>{cevir("Use these pages")}</Buton>
        )}
      </>
    );
  };

  const sonucHucresi = (c: MalzemeKartSertifikasi) => (
    <>
      {c.durum === "eklenebilir" && k.eylemler.ekle
        ? (
          <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `ekle-${c.sira}`} onClick={() => calistir(`ekle-${c.sira}`, () => api.post(MALZEME_UCU.ekle(satirId), { sira: c.sira }))}>
            {cevir("Add to package")}
          </Buton>
        )
        : malzemeDurumMetni(c, hedef)}
      {c.onay && <span className="block text-xs text-ink-3">{cevir("added: {0}", c.onay.yazilan)} · {cevir("Added by {0} on {1}", c.onay.kim, tarihSaat(c.onay.ts))}</span>}
    </>
  );

  const kayitSatiri = (c: MalzemeKartSertifikasi, s: MalzemeKayitSatiri) => {
    const pkg = c.yer.paket?.pkg ?? "—";
    const yazilabilir = s.durum === "yazilabilir";
    const secilen = s.alan === "heat" ? (heatSecimi[c.sira] ?? (s.adaylar.length === 1 ? s.adaylar[0]! : "")) : (s.adaylar[0] ?? "");
    const gonder = (alan: MalzemeKayitAlani, deger: string) => calistir(`kayit-${c.sira}-${alan}`, () => api.post(MALZEME_UCU.kayit(satirId), { sira: c.sira, alan, deger }));
    return (
      <li key={s.alan} className="flex flex-wrap items-center gap-1.5" data-kayit={s.alan}>
        <span className="min-w-0">{malzemeKayitMetni(s, pkg)}</span>
        {yazilabilir && k.eylemler.kaydet && (
          <>
            {s.alan === "heat" && s.adaylar.length > 1 && (
              <select className="rounded-md border border-line bg-surface px-1.5 py-0.5 text-xs" value={secilen} disabled={mesgul !== null}
                onChange={(e) => setHeatSecimi({ ...heatSecimi, [c.sira]: e.target.value })}>
                <option value="">{cevir("Choose…")}</option>
                {s.adaylar.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            {s.alan === "heat" && s.adaylar.length === 1 && <span className="font-mono text-xs">{s.adaylar[0]}</span>}
            <Buton kucuk disabled={mesgul !== null || !secilen} mesgul={mesgul === `kayit-${c.sira}-${s.alan}`} onClick={() => gonder(s.alan, secilen)}>
              {s.alan === "heat" ? cevir("Record heat") : cevir("Record PO")}
            </Buton>
          </>
        )}
        {yazilabilir && !k.eylemler.kaydet && <span className="w-full text-xs text-ink-3">{cevir("A PM or System administrator records heat and PO.")}</span>}
      </li>
    );
  };

  return (
    <div data-bolum="malzeme-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the material certificate for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.dosyaCinsi === "kitap" ? cevir("Certificate book") : o.sayfalar ? cevir("Material certificate p.{0}", malzemeAralikMetni(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              kapakli ? null : cevir("{0} certificates read", o.sertifikaSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="malzeme-baslik">
            <li className="flex gap-2">
              <Nokta k={o.dosyaCinsi === "belirsiz" ? "suphe" : "kesin"} />
              <span className="w-[10rem] shrink-0 text-ink-2">{cevir("File")}</span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{malzemeCinsMetni(o.dosyaCinsi)}{sayfaLinki(k.degerler.find((x) => x.alan === "kapak_mrb")?.sayfa ?? null)}</span>
            </li>
            {!kapakli && (() => {
              const d = k.degerler.find((x) => x.alan === "musteri_po");
              return (
                <li className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir("Customer PO")}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {d?.deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })()}
          </ul>

          {kapakli && (
            <Band tip="not" className="mt-2">
              {o.dosyaCinsi === "datapack"
                ? cevir("This file is not read as a material certificate: only its cover was read. File it as reference or on the riser’s Project documents; the datapack is read in a later step.")
                : cevir("This file is not read as a material certificate: only its cover was read. It is an OEM MRB: upload it as MRB (Complete PDF).")}
            </Band>
          )}

          {!kapakli && k.harita.length > 0 && (
            <>
              <AltBaslik>{cevir("Page map")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Index line")}</th>
                    <th className={TABLO_BASI}>{cevir("Part number")}</th>
                    <th className={TABLO_BASI}>{cevir("Pages in the index")}</th>
                    <th className={TABLO_BASI}>{cevir("Read")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.harita.map((h) => (
                      <tr key={h.no} data-indeks={h.no}>
                        <td className={cn(TABLO_HUCRESI, "font-mono")}>I{h.no}</td>
                        <td className={TABLO_HUCRESI}>{h.pn ?? "—"}</td>
                        <td className={TABLO_HUCRESI}>{h.sayfalar ?? "—"}{sayfaLinki(h.sayfa)}</td>
                        <td className={TABLO_HUCRESI}>{malzemeHaritaMetni(h)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">
                {cevir("Read in the book: the cover, the index and the material certificate pages of the part numbers in the stock catalogue. The CoC and PO pages are read with their own types later; DNV reports and appendices are not read.")}
              </p>
            </>
          )}

          {!kapakli && k.sertifikalar.length > 0 && (
            <>
              <AltBaslik>{cevir("Certificates")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Certificate")}</th>
                    <th className={TABLO_BASI}>{cevir("On the certificate")}</th>
                    <th className={TABLO_BASI}>{cevir("Stock package")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.sertifikalar.map((c) => {
                      const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
                      return (
                        <tr key={c.sira} data-sertifika={c.sira}>
                          <td className={cn(TABLO_HUCRESI, "font-mono")}>{cevir("{0} · p.{1}", c.sira, malzemeAralikMetni(c.sayfalar))}</td>
                          <td className={TABLO_HUCRESI}>
                            <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} /><span>{malzemeSertifikaMetni(c)}</span>{sayfaLinki(c.sayfalar.bas)}</span>
                            {c.tanik && <span className="block text-xs text-ink-3">{cevir("witness signed")}</span>}
                          </td>
                          <td className={TABLO_HUCRESI}>{paketHucresi(c)}</td>
                          <td className={TABLO_HUCRESI}>{sonucHucresi(c)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("OASIS proposes the package of the same part number whose heat / lot / SN holds a heat, a mill lot or the lot key of the certificate. Nothing is filed or recorded by itself.")}</p>
              {ekleVar && (
                <p className="mt-1 text-xs text-ink-3">
                  {cevir("“Add to package” files the pages of that certificate on its package too ({0}, Doc no. of the certificate). One certificate at a time; a package or a document already in Registers does not change. A certificate without a proposed package is filed on Registers by hand.", cevir(BELGE_TURU_ADI.stok_paket[k.sertifikalar.find((c) => c.turOnerisi)?.turOnerisi ?? "en10204_31"] ?? ""))}
                </p>
              )}

              {k.sertifikalar.map((c) => (
                <div key={c.sira} data-sertifika-ayrinti={c.sira}>
                  {c.heatler.length > 0 && (
                    <>
                      <AltBaslik>{cevir("Heats of certificate {0}", c.sira)}</AltBaslik>
                      <div className="overflow-x-auto rounded-md border border-line">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-line">
                            <th className={TABLO_BASI}>{cevir("Line")}</th>
                            <th className={TABLO_BASI}>{cevir("Heat")}</th>
                            <th className={TABLO_BASI}>{cevir("Mill lot · quantity")}</th>
                            <th className={TABLO_BASI}>{cevir("Handwritten mark")}</th>
                          </tr></thead>
                          <tbody className="divide-y divide-line">
                            {c.heatler.map((h) => (
                              <tr key={h.no}>
                                <td className={cn(TABLO_HUCRESI, "font-mono")}>H{h.no}</td>
                                <td className={TABLO_HUCRESI}><span className="inline-flex items-center gap-1.5"><Nokta k={h.heat?.kesinlik ?? "bos"} />{h.heat?.deger ?? "—"}</span>{sayfaLinki(h.heat?.sayfa ?? null)}</td>
                                <td className={TABLO_HUCRESI}>{[h.lot?.deger ?? "—", h.miktarMetni ?? "—"].join(" · ")}</td>
                                <td className={TABLO_HUCRESI}><span className="inline-flex items-center gap-1.5"><Nokta k={h.heatEl?.kesinlik ?? "bos"} />{h.heatEl?.deger ?? "—"}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {c.sonuclar.length > 0 && (
                    <>
                      <AltBaslik>{cevir("Results with a printed limit")}</AltBaslik>
                      <div className="overflow-x-auto rounded-md border border-line">
                        <table className="w-full text-sm">
                          <thead><tr className="border-b border-line">
                            <th className={TABLO_BASI}>{cevir("Line")}</th>
                            <th className={TABLO_BASI}>{cevir("Result")}</th>
                            <th className={TABLO_BASI}>{cevir("Printed limit")}</th>
                            <th className={TABLO_BASI}>{cevir("Inside")}</th>
                          </tr></thead>
                          <tbody className="divide-y divide-line">
                            {c.sonuclar.map((r) => (
                              <tr key={r.no}>
                                <td className={cn(TABLO_HUCRESI, "font-mono")}>R{r.no}</td>
                                <td className={TABLO_HUCRESI}>{[r.ozellik, r.sonuc].filter(Boolean).join(" ") || "—"}{sayfaLinki(r.sayfa)}</td>
                                <td className={TABLO_HUCRESI}>{[r.alt ? cevir("min {0}", r.alt) : null, r.ust ? cevir("max {0}", r.ust) : null].filter(Boolean).join(" · ") || "—"}</td>
                                <td className={TABLO_HUCRESI}>{r.icinde === null ? "—" : r.icinde ? "✓" : "✗"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {c.kayit && (
                    <div className="mt-2 rounded-md border border-line px-2.5 py-2" data-bolum="malzeme-kayit">
                      <p className="text-xs font-medium text-ink-2">{cevir("Record on the package")} · {cevir("Certificate {0}: {1}", c.sira, c.yer.paket?.pkg ?? "—")}</p>
                      <ul className="mt-1 space-y-1 text-sm">
                        {kayitSatiri(c, c.kayit.heat)}
                        {kayitSatiri(c, c.kayit.po)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
              {kayitVar && (
                <p className="mt-1 text-xs text-ink-3">
                  {cevir("Only a certain value of the certificate, only while the field of the package is empty; a value already on the package does not change. One field at a time; nothing is undone from this card (a PM or System administrator edits the package on Registers).")}
                </p>
              )}
            </>
          )}

          {yardim && !kapakli && secili !== null && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              {cok && (
                <label className="flex items-center gap-1.5 text-sm">
                  <span className="text-ink-2">{cevir("Certificate")}</span>
                  <select className="rounded-md border border-line bg-surface px-2 py-1 text-sm" value={secili} disabled={mesgul !== null} onChange={(e) => setSecim(Number(e.target.value))}>
                    {k.sertifikalar.map((c) => <option key={c.sira} value={c.sira}>{cevir("{0} · p.{1}", c.sira, malzemeAralikMetni(c.sayfalar))}</option>)}
                  </select>
                </label>
              )}
              <Buton kucuk disabled={mesgul !== null} onClick={() => {
                const f = malzemeFormu(k, secili);
                yardim.kullan(f.alanlar);
                if (f.sayfa) yardim.sayfalar(f.sayfa);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Doc no. with the certain certificate number and Pages with the pages of the certificate of this package when it is part of the PDF; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && mtl01 && (
            <Band tip="not" className="mt-2">{cevir("OASIS does not choose between the printed heat and the handwritten mark: both are shown and you decide. The decision is a record of its own; the package does not change.")}</Band>
          )}
          {yardim && kirmiziSertifika && hedefPaket && !hedefEsit(kirmiziSertifika.yer.yer, hedef) && (
            <Band tip="not" className="mt-2">
              {cevir("The red alert of certificate {0} does not stop filing the file on {1}: the filing gate looks at the alerts of the certificate of that package.", kirmiziSertifika.sira, hedefPaket.pkg)}
            </Band>
          )}
          {yardim && turFarki?.turOnerisi && (
            <Band tip="not" className="mt-2">
              {cevir("The certificate reads as {0}. Change “What is this file?” to {0} before filing, or file it as it is.", cevir(BELGE_TURU_ADI.stok_paket[turFarki.turOnerisi] ?? ""))}
            </Band>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: material grade, manufacturer, standard, buyer and the mill’s order number, certificate date, chemistry rows without a printed limit, signatures other than the 3.2 witness, and in a book the CoC and PO pages, DNV reports, appendices and the sections of part numbers that are not in the stock catalogue.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <div className="space-y-2">
                  {k.sertifikalar.filter((c) => c.uyarilar.length > 0).map((c) => (
                    <div key={c.sira} data-sertifika-uyari={c.sira}>
                      <p className="text-xs font-medium text-ink-2">{cevir("Certificate {0}: {1}", c.sira, c.pn ?? "—")}</p>
                      <ul className="mt-0.5 space-y-1.5">
                        {c.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                      </ul>
                    </div>
                  ))}
                  {k.karsilastirma.uyarilar.length > 0 && (
                    <ul className="space-y-1.5">
                      {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                    </ul>
                  )}
                </div>
              )}
          {gen11 && <p className="mt-1 text-xs text-ink-3">{cevir("A newer version is answered on Registers: mark the old certificate superseded there.")}</p>}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(MALZEME_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(MALZEME_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(MALZEME_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
